import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import TopNav from "@/components/TopNav";
import { PhoneMockup } from "@/components/PhoneMockup";
import { LOGO_URL, LEARN_MORE_FEATURES, ROOTED_PRO } from "@/lib/rooted";

/**
 * Individual feature row — image on one side, copy on the other, phone slightly tilted.
 * Alternates side and tilt direction for rhythm.
 */
const FeatureRow = ({ feature, index }) => {
  const side = index % 2 === 0 ? "left" : "right";
  const tilt = index % 2 === 0 ? -5 : 5;
  const accent = feature.accent;

  return (
    <section
      data-testid={`learn-more-feature-${feature.key}`}
      className="bg-rooted py-16 md:py-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Visual */}
          <div
            className={`lg:col-span-6 flex justify-center ${
              side === "right" ? "lg:order-2" : "lg:order-1"
            }`}
          >
            {feature.image || feature.video ? (
              <div className="relative">
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-12 rounded-full"
                  style={{
                    background: `radial-gradient(circle at center, ${accent}22 0%, ${accent}0A 45%, rgba(0,0,0,0) 70%)`,
                  }}
                />
                <PhoneMockup
                  testId={`learn-more-phone-${feature.key}`}
                  image={feature.image}
                  video={feature.video}
                  tilt={tilt}
                  size="md"
                />
              </div>
            ) : (
              // Placeholder card for features without a screenshot yet
              <div
                data-testid={`learn-more-placeholder-${feature.key}`}
                className="w-[280px] md:w-[320px] aspect-[9/19.2] rounded-[3rem] border border-[#1f1f1f] bg-gradient-to-b from-[#0a0a0a] to-[#050505] grid place-items-center"
                style={{ transform: `rotate(${tilt}deg)` }}
              >
                <div className="text-center px-8">
                  <img src={LOGO_URL} alt="" className="h-14 w-14 mx-auto opacity-80" />
                  <div className="verse-cite mt-6" style={{ color: accent }}>
                    Preview coming soon
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Copy */}
          <div
            className={`lg:col-span-6 ${
              side === "right" ? "lg:order-1" : "lg:order-2"
            }`}
          >
            <span
              className="verse-cite block mb-4 tracking-[0.22em]"
              style={{ color: accent }}
            >
              0{index + 1} · Learn more
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-rooted mb-6">
              {feature.title}
            </h2>
            <p className="font-body text-lg md:text-xl leading-[1.75] text-muted-rooted max-w-xl">
              {feature.summary}
            </p>
            <div
              className="mt-8 h-[2px] w-16"
              style={{ backgroundColor: accent }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function FeaturesPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="page-features" className="bg-rooted min-h-screen">
      <TopNav />

      {/* Header */}
      <section className="relative overflow-hidden bg-rooted pt-28 md:pt-36 pb-10 md:pb-14">
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(57,151,26,0.14) 0%, rgba(57,151,26,0.04) 35%, rgba(0,0,0,0) 65%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="features-back-link"
            className="inline-flex items-center gap-2 text-muted-rooted hover:text-[#39971a] font-display text-sm tracking-wide transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </Link>

          <div className="text-center mt-12 md:mt-16">
            <img
              src={LOGO_URL}
              alt="Rooted"
              className="h-14 w-14 md:h-16 md:w-16 mx-auto drop-shadow-[0_0_30px_rgba(57,151,26,0.4)] mb-8"
            />
            <span className="verse-cite brand-green block mb-4">Learn More</span>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-[-0.02em] text-rooted mb-6">
              Everything else, up close.
            </h1>
            <p className="font-body text-lg md:text-xl text-muted-rooted max-w-2xl mx-auto leading-[1.7]">
              Expansion features that live inside Rooted for anyone who wants to go a little deeper.
            </p>
          </div>
        </div>
      </section>

      {/* Feature rows */}
      <div className="bg-rooted">
        {LEARN_MORE_FEATURES.map((f, i) => (
          <FeatureRow key={f.key} feature={f} index={i} />
        ))}
      </div>

      {/* Rooted Pro — text-only section */}
      <section
        data-testid="learn-more-rooted-pro"
        className="relative bg-rooted py-24 md:py-32 border-t border-[#f2ede6]/8 overflow-hidden"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(200,155,60,0.14) 0%, rgba(200,155,60,0.04) 40%, rgba(0,0,0,0) 70%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14 md:mb-16">
            <span
              className="verse-cite block mb-4 tracking-[0.22em]"
              style={{ color: ROOTED_PRO.accent }}
            >
              Membership · Rooted Pro
            </span>
            <h2 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-[-0.02em] text-rooted mb-6">
              {ROOTED_PRO.title}
            </h2>
            <p className="font-body text-lg md:text-xl text-muted-rooted max-w-2xl mx-auto leading-[1.7]">
              {ROOTED_PRO.headline}
            </p>
            <p className="mt-5 font-body text-base md:text-lg text-soft-rooted max-w-2xl mx-auto leading-[1.7]">
              {ROOTED_PRO.intro}
            </p>
          </div>

          <div
            className="rounded-lg border p-8 md:p-12 bg-gradient-to-b from-[#0a0a0a] to-[#050505]"
            style={{ borderColor: `${ROOTED_PRO.accent}33` }}
          >
            <ul className="space-y-8 md:space-y-10">
              {ROOTED_PRO.features.map((f, i) => (
                <li
                  key={f.title}
                  data-testid={`rooted-pro-feature-${i}`}
                  className="grid md:grid-cols-12 gap-4 md:gap-8 items-start"
                >
                  <div className="md:col-span-4 flex items-baseline gap-3">
                    <span
                      className="font-display text-2xl leading-none tabular-nums"
                      style={{ color: ROOTED_PRO.accent }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl md:text-2xl text-rooted tracking-[-0.01em]">
                      {f.title}
                    </span>
                  </div>
                  <div className="md:col-span-8 font-body text-base md:text-lg leading-[1.75] text-muted-rooted">
                    {f.body}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-rooted py-20 md:py-24 text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="font-display text-2xl md:text-4xl leading-[1.35] text-rooted mb-8">
            Rooted is still being built.
            <br />
            <span className="text-muted-rooted">Be part of it from the beginning.</span>
          </p>
          <Link
            to="/#waitlist"
            data-testid="features-cta-waitlist"
            className="btn-pill text-base md:text-lg px-8 md:px-10 py-3.5 md:py-4"
          >
            Join Waitlist
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="section-footer" className="bg-rooted">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rooted" className="h-9 w-9" />
            <div>
              <div className="font-display text-lg text-rooted">Rooted</div>
              <div className="verse-cite mt-1">Bible Together.</div>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 font-display text-sm text-muted-rooted">
            <Link to="/privacy" className="hover:text-[#39971a]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#39971a]">Terms of Use</Link>
            <span className="text-muted-rooted/40">·</span>
            <span>Built by BeyondBible</span>
          </nav>
        </div>
        <div className="border-t border-[#f2ede6]/5">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 verse-cite flex justify-between">
            <span>© {new Date().getFullYear()} Rooted</span>
            <span className="hidden md:inline">Coming Soon · In Development</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
